// src/components/layout/Footer.jsx
import React from "react";
import { useSettings } from "../../contexts/SettingsContext";
import { 
    Facebook, Instagram, Twitter, Youtube, Linkedin, 
    Github, Globe, Mail, Phone 
} from 'lucide-react';

/* ===================== EFEITOS VISUAIS ===================== */
const RenderEffectOverlay = ({ effect, neonColor }) => {
  if (effect === "rain") {
    return (
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-0">
        {[...Array(40)].map((_, i) => (
          <div
            key={i}
            className="absolute w-[2px] h-12 bg-white opacity-25 animate-rain"
            style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 2}s`,
              animationDuration: `${1 + Math.random() * 2}s`,
            }}
          />
        ))}
      </div>
    );
  }

  if (effect === "stars") {
    return (
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-0">
        {[...Array(60)].map((_, i) => (
          <div
            key={i}
            className="absolute w-[3px] h-[3px] bg-white rounded-full animate-twinkle opacity-50"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 4}s`,
            }}
          />
        ))}
      </div>
    );
  }

  if (effect === "neon" && neonColor) {
    return (
      <div
        className="absolute inset-0 pointer-events-none z-0"
        style={{ boxShadow: `0 0 60px ${neonColor}40 inset` }}
      />
    );
  }

  return null;
};

/* ===================== ICON MAPPER ===================== */
const socialMap = {
    facebook: <Facebook size={20} />,
    instagram: <Instagram size={20} />,
    twitter: <Twitter size={20} />,
    x: <Twitter size={20} />,
    youtube: <Youtube size={20} />,
    linkedin: <Linkedin size={20} />,
    github: <Github size={20} />,
    web: <Globe size={20} />,
    email: <Mail size={20} />,
    phone: <Phone size={20} />
};

/* ===================== FOOTER ===================== */
export default function Footer() {
  const { settings, loading } = useSettings();

  if (loading || (settings?.FOOTER_ENABLED !== undefined && !settings.FOOTER_ENABLED)) return null;

  /* ===================== ZONES ===================== */
  let zones = { zone1: [], zone2: [], zone3: [], zone4: [] };

  try {
    if (settings.FOOTER_ZONES) {
      const parsed =
        typeof settings.FOOTER_ZONES === "string"
          ? JSON.parse(settings.FOOTER_ZONES)
          : settings.FOOTER_ZONES;

      zones = { ...zones, ...(parsed || {}) };
    }
  } catch (e) {
    console.warn("Erro ao parsear FOOTER_ZONES", e);
  }

  /* ===================== VISUAL ===================== */
  const {
    FOOTER_BG_COLOR,
    FOOTER_BG_GRADIENT,
    FOOTER_TEXT_COLOR,
    FOOTER_EFFECT,
    FOOTER_NEON_COLOR,
    FOOTER_MODE
  } = settings || {};

  const textColor = FOOTER_TEXT_COLOR && FOOTER_TEXT_COLOR !== "" ? FOOTER_TEXT_COLOR : "#ffffff";

  const containerStyle = {
    position: "relative",
    padding: "60px 0",
    color: textColor,
    background:
      FOOTER_BG_GRADIENT?.trim()
        ? FOOTER_BG_GRADIENT
        : FOOTER_BG_COLOR?.trim()
        ? FOOTER_BG_COLOR
        : "#111827",
  };

  if (FOOTER_EFFECT === "glass") {
    containerStyle.backdropFilter = "blur(12px)";
    containerStyle.backgroundColor = FOOTER_BG_COLOR || "rgba(17,24,39,0.7)";
    containerStyle.borderTop = "1px solid rgba(255,255,255,0.1)";
  }

  const showBuilder = !FOOTER_MODE || FOOTER_MODE === "builder";

  return (
    <footer className="w-full overflow-hidden" style={containerStyle}>
      <RenderEffectOverlay effect={FOOTER_EFFECT} neonColor={FOOTER_NEON_COLOR} />

      <div className="container mx-auto px-6 relative z-10" style={{ color: textColor }}>
        
        {showBuilder && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 text-sm">
            {['zone1', 'zone2', 'zone3', 'zone4'].map((zoneId) => (
              <div key={zoneId} className="space-y-6">
                {(zones[zoneId] || [])
                  .filter((w) => w.enabled)
                  .map((w, i) => {
                    const lines = (w.content || "")
                      .split("\n")
                      .map((l) => l.trim())
                      .filter(Boolean);

                    return (
                      <div key={i} className="flex flex-col gap-2">
                        {w.title && (
                          <h4 className="font-bold text-lg mb-2 uppercase tracking-wider opacity-100" style={{ color: textColor }}>
                            {w.title}
                          </h4>
                        )}

                        {w.type === "text" && (
                          <p className="leading-relaxed opacity-80 whitespace-pre-line" style={{ color: textColor }}>
                            {w.content}
                          </p>
                        )}

                        {w.type === "address" && (
                          <p className="flex items-start gap-2 opacity-80 whitespace-pre-line" style={{ color: textColor }}>
                            <span className="text-lg">📍</span> {w.content}
                          </p>
                        )}

                        {w.type === "links" && (
                          <div className="flex flex-col gap-2">
                            {lines.map((ln, x) => {
                              const [label, href] = ln.split("|").map((s) => s.trim());
                              return (
                                <a key={x} href={href || "#"} className="hover:opacity-100 hover:underline flex items-center gap-2 opacity-80 transition-all" style={{ color: textColor }}>
                                  <span className="opacity-50 text-xs">›</span> {label}
                                </a>
                              );
                            })}
                          </div>
                        )}

                        {w.type === "social" && (
                          <div className="flex flex-wrap gap-4 mt-1">
                            {lines.map((ln, x) => {
                              const [label, href, iconKey] = ln.split("|").map((s) => s.trim());
                              const key = iconKey?.toLowerCase() || label?.toLowerCase();
                              const IconComponent = socialMap[key] || <Globe size={20} />;
                              
                              return (
                                <a
                                  key={x}
                                  href={href || "#"}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="hover:scale-110 transition-transform opacity-80 hover:opacity-100"
                                  title={label}
                                  style={{ color: textColor }}
                                >
                                  {IconComponent}
                                </a>
                              );
                            })}
                          </div>
                        )}

                        {w.type === "map" && (
                          <div className="w-full mt-2">
                            {(() => {
                              const address = lines[0];
                              if (!address) return null;
                              const mapSrc = `https://maps.google.com/maps?q=${encodeURIComponent(address)}&t=&z=13&ie=UTF8&iwloc=&output=embed`;
                              return (
                                <iframe
                                  title={`Mapa ${w.title}`}
                                  src={mapSrc}
                                  className="w-full h-40 rounded-xl shadow-lg border border-white/10"
                                  loading="lazy"
                                  style={{ filter: "grayscale(10%) contrast(1.1)" }}
                                />
                              );
                            })()}
                          </div>
                        )}

                        {w.type === "html" && (
                          <div className="prose prose-sm max-w-none prose-invert opacity-90" style={{ color: textColor }} dangerouslySetInnerHTML={{ __html: w.content }} />
                        )}
                      </div>
                    );
                  })}
              </div>
            ))}
          </div>
        )}

        {/* Bottom Bar: Forçando a cor do BD */}
        <div 
            className="mt-16 pt-8 border-t text-center text-xs flex flex-col md:flex-row justify-between items-center gap-4"
            style={{ borderColor: `${textColor}40`, color: textColor }}
        >
          <div 
            style={{ color: textColor }}
            dangerouslySetInnerHTML={{ __html: settings.FOOTER_TEXT || `© ${new Date().getFullYear()} ${settings.SITE_NAME || "Imagens da Face"}. Todos os direitos reservados.` }} 
          />
          
          <div className="flex gap-4">
             {settings.FOOTER_LINKS && settings.FOOTER_MODE === 'simple' && settings.FOOTER_LINKS.split('\n').map((link, i) => {
                const [label, href] = link.split('|').map(p => p.trim());
                return label && href ? <a key={i} href={href} className="hover:underline" style={{ color: textColor }}>{label}</a> : null;
             })}
          </div>
        </div>
      </div>
    </footer>
  );
}